import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { WorkflowSteps } from "@/components/WorkflowSteps";
import { CheckCircle2, Loader2, XCircle, Clock, Activity, Eye } from "lucide-react";

const DOT = {
  done: "bg-emerald-500", finished: "bg-emerald-500",
  dispatched: "bg-blue-500 animate-pulse", running: "bg-blue-500 animate-pulse",
  waiting: "bg-amber-500 animate-pulse", pending: "bg-zinc-300",
  error: "bg-red-500", stopped: "bg-red-500",
};
const WF_ICON = {
  finished: <CheckCircle2 size={14} className="text-emerald-600" />,
  running: <Loader2 size={14} className="animate-spin text-blue-600" />,
  error: <XCircle size={14} className="text-red-600" />,
};

export const WorkflowTracker = () => {
  const { t } = useLang();
  const sl = (s) => (t(`st.${s}`) === `st.${s}` ? s : t(`st.${s}`));
  const { isAdmin } = useAuth();
  const [wfs, setWfs] = useState([]);
  const [busy, setBusy] = useState("");
  const activeRef = useRef(true);

  const tick = () => api.get("/workflows?limit=6").then((r) => activeRef.current && setWfs(r.data)).catch(() => {});
  useEffect(() => {
    activeRef.current = true;
    tick();
    const id = setInterval(tick, 4000);
    return () => { activeRef.current = false; clearInterval(id); };
  }, []);

  const retryStep = async (wid, idx) => {
    setBusy(wid + idx);
    try { await api.post(`/workflows/${wid}/steps/${idx}/retry`); toast.success(t("jobs.retried")); tick(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-zinc-200 bg-white" data-testid="workflow-tracker">
      <div className="flex items-center gap-2 border-b border-zinc-200 bg-zinc-900 px-5 py-3">
        <Activity size={14} className="text-emerald-400" />
        <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-white">{t("dash.liveWorkflows")}</h2>
      </div>
      <div className="divide-y divide-zinc-100">
        {wfs.length === 0 && <div className="px-5 py-6 text-center text-sm text-zinc-400">{t("dash.noWorkflows")}</div>}
        {wfs.map((w) => (
          <Dialog key={w.id}>
            <DialogTrigger asChild>
              <button type="button" className="group block w-full px-5 py-3 text-left transition-colors hover:bg-zinc-50" data-testid={`tracker-wf-${w.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    {WF_ICON[w.status] || <Clock size={14} className="text-zinc-400" />}
                    <span className="truncate text-sm font-medium text-zinc-800">{w.title}</span>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-400">{sl(w.status)}</span>
                    <Eye size={13} className="text-zinc-300 transition-colors group-hover:text-primary" />
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  {w.steps.map((st, i) => (
                    <span key={`${st.key || "step"}-${i}`} title={`${st.label} — ${st.status}`}
                      className={`h-1.5 flex-1 rounded-full ${DOT[st.status] || "bg-zinc-200"}`} />
                  ))}
                </div>
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-sm sm:max-w-2xl" data-testid={`tracker-dialog-${w.id}`}>
              <DialogHeader>
                <DialogTitle className="text-sm">{w.title} · {sl(w.status)}</DialogTitle>
                <DialogDescription className="font-mono text-[11px]">{w.id}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[70vh] space-y-2 overflow-y-auto">
                <WorkflowSteps wf={w} isAdmin={isAdmin} onRetryStep={retryStep} busy={busy} />
              </div>
            </DialogContent>
          </Dialog>
        ))}
      </div>
    </div>
  );
};
